/**
 * 鼠标休眠/会话 stableId 身份判定（匠盟 / 贝盈）。
 * 仅支持：精确相等，或方案内身份字段一致；不做前缀/基准 id 兜底。
 */

export interface JiangmengMouseIdentity {
  cid: number;
  mid: number;
  type: number;
}

export interface BeiyingMouseIdentity {
  vendorId: number;
  productId: number;
  uuid: string;
}

/** 解析匠盟 stableId 尾部 cid/mid/type（mouse-vid-pid-serial-cid-mid-type） */
export function parseJiangmengMouseIdentity(
  stableId: string | null | undefined
): JiangmengMouseIdentity | null {
  if (!stableId) return null;
  const parts = stableId.split("-");
  if (parts[0] !== "mouse" || parts.length < 7) return null;
  const cid = Number(parts[parts.length - 3]);
  const mid = Number(parts[parts.length - 2]);
  const type = Number(parts[parts.length - 1]);
  if (
    !Number.isFinite(cid) ||
    !Number.isFinite(mid) ||
    !Number.isFinite(type)
  ) {
    return null;
  }
  return { cid, mid, type };
}

/** 解析贝盈 stableId（beiying-vid-pid-uuid） */
export function parseBeiyingMouseIdentity(
  stableId: string | null | undefined
): BeiyingMouseIdentity | null {
  if (!stableId || !stableId.startsWith("beiying-")) return null;
  const parts = stableId.split("-");
  if (parts.length < 4) return null;
  const vendorId = Number(parts[1]);
  const productId = Number(parts[2]);
  const uuid = parts.slice(3).join("-");
  if (!Number.isFinite(vendorId) || !Number.isFinite(productId) || !uuid) {
    return null;
  }
  return { vendorId, productId, uuid };
}

/** 两台匠盟鼠标是否为同一物理设备（stableId 精确相等或 cid/mid/type 一致） */
export function jiangmengMouseStableIdsMatch(
  a: string | null | undefined,
  b: string | null | undefined
): boolean {
  if (!a || !b) return false;
  if (a === b) return true;
  const ia = parseJiangmengMouseIdentity(a);
  const ib = parseJiangmengMouseIdentity(b);
  if (!ia || !ib) return false;
  return ia.cid === ib.cid && ia.mid === ib.mid && ia.type === ib.type;
}

/**
 * 同一匠盟产品型号（cid + mid 一致；连接 type 可不同，如有线与 2.4G 各一条 stableId）。
 * 用于 UI 卡片 stableId 与 HID 实例 stableId 不完全一致时仍允许同步 store。
 */
export function jiangmengMouseProductIdsMatch(
  a: string | null | undefined,
  b: string | null | undefined
): boolean {
  if (!a || !b) return false;
  if (a === b) return true;
  const ia = parseJiangmengMouseIdentity(a);
  const ib = parseJiangmengMouseIdentity(b);
  if (!ia || !ib) return false;
  return ia.cid === ib.cid && ia.mid === ib.mid;
}

/** 两台贝盈鼠标是否为同一物理设备（stableId 精确相等或 vid/pid/uuid 一致） */
export function beiyingMouseStableIdsMatch(
  a: string | null | undefined,
  b: string | null | undefined
): boolean {
  if (!a || !b) return false;
  if (a === b) return true;
  const ia = parseBeiyingMouseIdentity(a);
  const ib = parseBeiyingMouseIdentity(b);
  if (!ia || !ib) return false;
  return (
    ia.vendorId === ib.vendorId &&
    ia.productId === ib.productId &&
    ia.uuid === ib.uuid
  );
}

/** 休眠事件 stableId 是否属于当前会话/卡片（同方案内身份匹配，禁止跨方案/前缀兜底） */
export function mouseSleepStableIdsMatch(
  sessionStableId: string | null | undefined,
  eventStableId: string | null | undefined
): boolean {
  if (!sessionStableId || !eventStableId) return false;
  if (sessionStableId === eventStableId) return true;
  if (
    sessionStableId.startsWith("beiying-") &&
    eventStableId.startsWith("beiying-")
  ) {
    return beiyingMouseStableIdsMatch(sessionStableId, eventStableId);
  }
  if (
    sessionStableId.startsWith("mouse-") &&
    eventStableId.startsWith("mouse-")
  ) {
    return jiangmengMouseStableIdsMatch(sessionStableId, eventStableId);
  }
  return false;
}
